import * as React from "react";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import { Box, FormLabel, MenuItem } from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { ROLES, User } from "types/user";
import { VisuallyHiddenInput } from "components/Input";
import { Img } from "components/Img";
import { Person, Upload } from "@mui/icons-material";
import { TagManager } from "components/TagManager";
import { ListWithEntitySelector } from "components/ListWithEntitySelector";
import { FieldWithEntitySelector } from "components/FieldWithEntitySelector";
import { Organization } from "types/organization";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { MainLayoutContainer } from "components/Layout";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { ICreateOrUpdateUserCredentials } from "types/auth";
import { useUserEdit } from "./useUserEdit";
import { Link } from "react-router-dom";
import { isRunningInNativeApp, isMobile } from "../../../utils/UserDevice";
import { normalizeEmail } from "utils/email";

const StyledFormicError = styled("p")(() => ({
  color: "red",
  padding: 0,
  margin: 0,
}));

const initialValues = {
  name: "",
  email: "",
  role: ROLES.ADMIN,
};

const getValidationSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("This field is required"),
    email: Yup.string()
      .email("Invalid email")
      .required("This field is required"),
    role: Yup.string().required("This field is required"),
  });

export const UserEdit = () => {
  const {
    userLoading,
    userToEdit,
    loading,
    avatar,
    errorImage,
    uploading,
    onUploadFile,
    UploadFile,
    tags,
    handleAddTag,
    handleDeleteTag,
    organizations,
    selectedOrganization,
    setSelectedOrganization,
    selectedCoaches,
    setSelectedCoaches,
    coaches,
    handleEditUser,
  } = useUserEdit();

  const formik = useFormik({
    initialValues: userToEdit || initialValues,
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const reqValues = {
        user_id: userToEdit?.user_id,
        name: values.name.trim(),
        email: normalizeEmail(values.email),
        role: values.role,
      };
      const picture_url = await UploadFile();
      if (picture_url === "ERROR") return;
      handleEditUser(
        reqValues as ICreateOrUpdateUserCredentials,
        picture_url,
      ).then();
    },
  });

  if (userLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!userToEdit) {
    return (
      <Container>
        <Typography variant="h5" component="h1" gutterBottom>
          User not found
        </Typography>
      </Container>
    );
  }

  return (
    <MainLayoutContainer title="Edit user" breadcrumb={BREADCRUMB_PAGE.USER}>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
            pt={1}
          >
            <Box>
              <Img src={avatar} width={80} height={80} icon={Person} circle />
            </Box>
            <Box ml={2}>
              <Box mb={1} fontWeight={600}>
                Change Profile Picture
              </Box>
              {isMobile() || isRunningInNativeApp() ? (
                <Typography variant="body2" color="textSecondary">
                  To change profile picture, please use a desktop device.
                </Typography>
              ) : (
                <Button
                  disabled={uploading}
                  component="label"
                  role={undefined}
                  variant="outlined"
                  tabIndex={-1}
                  startIcon={<Upload />}
                >
                  Upload image
                  <VisuallyHiddenInput type="file" onChange={onUploadFile} />
                </Button>
              )}
              {errorImage !== "" && (
                <Box mt={1} sx={{ color: "red" }}>
                  {errorImage}
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            <TextField
              variant="outlined"
              onChange={formik.handleChange}
              margin="normal"
              required
              fullWidth
              name="name"
              label="Name"
              type="text"
              id="name"
              value={formik?.values?.name}
            />
            <ErrorMessage name="name" component={StyledFormicError} />

            <TextField
              variant="outlined"
              onChange={formik.handleChange}
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              disabled={true}
              value={formik?.values?.email}
            />
            <ErrorMessage name="email" component={StyledFormicError} />

            <TextField
              variant="outlined"
              onChange={formik.handleChange}
              margin="normal"
              required
              fullWidth
              id="role"
              name="role"
              select
              label="Role"
              autoComplete="role"
              color={"primary"}
              value={formik?.values?.role}
            >
              {Object.values(ROLES).map((role: ROLES) => (
                <MenuItem key={role} value={role} color={"primary"}>
                  {role.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
            <ErrorMessage name="role" component={StyledFormicError} />
            <Box mt={2}>
              <FormLabel>Tags</FormLabel>
              <TagManager
                tags={tags}
                onAdd={handleAddTag}
                onDelete={handleDeleteTag}
                isEditing
              />
            </Box>
            <Box mt={2}>
              <FieldWithEntitySelector
                label="Organization"
                idField="org_id"
                entityName="organization"
                element={selectedOrganization}
                onChangeElement={(selected: Organization) =>
                  setSelectedOrganization(selected)
                }
                entityList={organizations}
                displayField="name"
                compact
              />
            </Box>
            {formik?.values?.role === ROLES.STUDENT && (
              <Box mt={2}>
                <FormLabel>Mentors</FormLabel>
                <ListWithEntitySelector
                  elements={selectedCoaches}
                  onChangeElements={(users: User[]) =>
                    setSelectedCoaches(users)
                  }
                  idField="user_id"
                  entityName="Mentor"
                  entityList={coaches}
                  displayField="name"
                  displayFieldSecondary="email"
                />
              </Box>
            )}

            <Grid sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Box mr={2}>
                <Button variant="outlined" component={Link} to="/users">
                  Cancel
                </Button>
              </Box>

              <Box>
                <Button
                  disabled={loading}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Save Changes
                </Button>
              </Box>
              {loading && (
                <CircularProgress
                  size={30}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Grid>
          </Box>
        </form>
      </FormikProvider>
    </MainLayoutContainer>
  );
};
